import { useTranslation } from 'next-i18next';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useModal } from '@app/components/modal-views/context';
import { toast } from 'react-toastify';
import { ToastId } from '@app/constants/ToastId';
import { ProjectListHighlights } from '@app/constants/Constants';
import ProfileImage from '@app/components/auth/profile-image';
import PdfEditorTextField from '@app/components/ui/input/pdf-editor-text-field';
import Button from '@app/components/ui/button';
import { useAddNewOrganizationMutation } from '@app/store/organization/api';
import { OrganizationDto } from '@app/models/dtos/organization-dtos';

export default function AddOrganizationModal() {
    const initialOrganizationObj = { name: 'Default', description: '' };

    const [newOrganizationObj, setnewOrganizationObj] = useState<any>(initialOrganizationObj);

    const projectFieldRef = useRef<HTMLInputElement | null>(null);

    const { t } = useTranslation(['organization', 'common']);
    const { closeModal } = useModal();

    useEffect(() => {
        if (projectFieldRef.current) {
            projectFieldRef.current.focus();
        }
    }, []);

    const [createNewOrganization, { isLoading }] = useAddNewOrganizationMutation();

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setnewOrganizationObj({ ...newOrganizationObj, [event.target.id]: event.target.value });
    };

    async function handleApiCall(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const requestBody: OrganizationDto = {
            ...newOrganizationObj,
            handle: newOrganizationObj.name.toLowerCase() as string,
            type: 'primary'
        };

        const response = await createNewOrganization(requestBody);

        if ('error' in response) {
            // @ts-ignore
            if (response.error.status === 409) toast.error(`${t('toast.organizationNameAlreadyExist', { ns: 'common' })}`, { toastId: ToastId.ORGANIZATION_ERROR_TOAST });
            else toast.error(`${t('toast.errorCreatingNewOrganization', { ns: 'common' })}`, { toastId: ToastId.ORGANIZATION_ERROR_TOAST });
        }
        if ('data' in response) {
            toast.success(`${t('toast.organizationCreated', { ns: 'common' })}`, { toastId: ToastId.ORGANIZATION_SUCCESS_TOAST });
            closeModal();
        }
    }

    const handleCancel = (e: any) => {
        e.preventDefault();
        closeModal();
    };

    function getColorForProject(name: string) {
        const hash = name.charCodeAt(0);
        return ProjectListHighlights[hash % ProjectListHighlights.length];
    }

    function getProfileAvatarColor(name: string) {
        const finalName = name ? name : 'Default';
        const selectedColor = getColorForProject(finalName);
        return {
            color: selectedColor['focus'],
            borderRadius: '0.375rem',
            backgroundColor: selectedColor['background'] + ' !important',
            fontWeight: 700,
            paddingTop: '0.1875rem',
            paddingBottom: '0.1875rem',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem'
        };
    }

    return (
        <div className={'relative flex flex-col gap-2 rounded-lg border-[1px] border-[#FBFCFE] bg-white'}>
            <div className={'m-1 rounded-lg bg-gray-100 p-1'}>
                <div className={'absolute right-4 top-2 cursor-pointer pl-3 text-xl'} onClick={handleCancel}>
                    &#x2715;
                </div>
                <div className="p-3">
                    <h1 className={'text-xl font-bold'}>{t('addNewOrganization.title')}</h1>
                    <p className={'max-w-screen-2xs break-words pt-2 !font-normal text-gray-500'}>{t('addNewOrganization.desc')}</p>
                </div>
            </div>
            <div className="px-2 pb-4">
                <ProfileImage size={64} style={getProfileAvatarColor(newOrganizationObj.name)} image={null} name={newOrganizationObj.name ? newOrganizationObj.name : 'Default'} />
                <form onSubmit={handleApiCall}>
                    <p className="p-2 text-sm font-normal">{t('addNewOrganization.inputHeader')}</p>
                    <PdfEditorTextField
                        data-testid="enter-organization-name"
                        id={'name'}
                        inputRef={projectFieldRef}
                        placeholder={t('label.enterOrganizationName', { ns: 'common' })}
                        value={newOrganizationObj.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e)}
                        className={'!w-full !rounded-md '}
                    />
                    <Button data-testid="next-button" className={'mt-2 flex-1'} variant={'solid'} fullWidth isLoading={isLoading} disabled={!newOrganizationObj.name} type="submit">
                        {t('addNewOrganization.btn')}
                    </Button>
                </form>
            </div>
        </div>
    );
}
